/**
 * CheckStage — Consumer Context Check (deterministic, non-scam).
 *
 * IMPORTANT — per product specification this stage is the Consumer
 * Context Check ONLY. It reads the consumer's Financial Profile and
 * produces one of three deterministic outcomes:
 *   • informational (profile missing or incomplete)
 *   • clear         (commitment fits within disposable income)
 *   • pressure      (fires the approved message: "This commitment may
 *                    place additional pressure on your current finances.")
 *
 * It does NOT run scam detection, product-risk conclusions, or
 * rate-based signals. Scam indicators live in the separate Trust &
 * Safety / "Check Something Suspicious" surface.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Wallet, Landmark, Info, CheckCircle2, AlertTriangle, ArrowRight, UserCog,
} from "lucide-react";
import { NAVY, GOLD } from "@/components/wallet/WalletShell";
import { fetchCheck, reviewCheck, zar } from "@/lib/wallet-decisions";
import NaviFace from "@/components/wallet/NaviFace";

const OUTCOME_STYLES = {
  clear:         { color: "#059669", bg: "#ECFDF5", border: "#A7F3D0", label: "Fits comfortably", icon: CheckCircle2 },
  pressure:      { color: "#B45309", bg: "#FFFBEB", border: "#FDE68A", label: "Additional pressure", icon: AlertTriangle },
  informational: { color: "#334155", bg: "#F8FAFC", border: "#E2E8F0", label: "Not enough context",  icon: Info },
};

export default function CheckStage({ decision, deviceId, onAdvance }) {
  const done = decision.status !== "CHECK_PENDING";
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(decision.check_review?.consumer_note || "");

  useEffect(() => {
    let c = false;
    (async () => {
      try {
        const d = await fetchCheck(decision.decision_id, deviceId);
        if (!c) setData(d);
      } catch (e) { if (!c) setErr(e?.response?.data?.detail || "Could not load."); }
    })();
    return () => { c = true; };
  }, [decision.decision_id, deviceId]);

  async function submit() {
    if (busy || done) return;
    setBusy(true); setErr(null);
    try {
      const updated = await reviewCheck(decision.decision_id, deviceId, note || null);
      onAdvance(updated);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not save.");
    } finally { setBusy(false); }
  }

  if (!data && !err) {
    return <div className="rounded-2xl border border-slate-100 bg-white p-8 text-center animate-pulse text-slate-400" data-testid="check-loading">Running Consumer Context Check…</div>;
  }
  if (err) return <div className="text-[12px] text-red-700" data-testid="check-error">{err}</div>;

  // If we're done, use the stored review; otherwise the fresh context
  const ctx = done ? (decision.check_review?.context || data.context) : data.context;
  const style = OUTCOME_STYLES[ctx.outcome] || OUTCOME_STYLES.informational;
  const Icon = style.icon;

  return (
    <section className="space-y-4" data-testid="check-stage">
      {/* NAVI intro */}
      <div className="rounded-2xl border border-slate-100 bg-white p-4 flex items-start gap-3">
        <NaviFace size="md" />
        <div className="min-w-0">
          <div className="text-[13px] font-semibold" style={{ color: NAVY }}>Now let's see how this proposal fits YOUR context.</div>
          <div className="text-[12px] text-slate-600 mt-1 leading-relaxed">
            This is a private check — I'm comparing the monthly commitment against the disposable income you shared in your Financial Profile. Nothing here is sent to the institution or the adviser.
          </div>
        </div>
      </div>

      {/* Outcome banner */}
      <div
        className="rounded-2xl border p-5 flex items-start gap-3"
        style={{ background: style.bg, borderColor: style.border }}
        data-testid={`check-outcome-${ctx.outcome}`}
      >
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "white" }}>
          <Icon className="w-5 h-5" style={{ color: style.color }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10.5px] uppercase tracking-[0.22em] font-semibold" style={{ color: style.color }}>
            Consumer Context · {style.label}
          </div>
          <div className="mt-1 font-display text-[15px] font-semibold" style={{ color: NAVY }}>
            {ctx.message}
          </div>
        </div>
      </div>

      {/* Numbers block */}
      {ctx.profile_complete && (
        <div className="rounded-2xl border border-slate-100 bg-white p-4 lg:p-5" data-testid="check-numbers">
          <div className="text-[10.5px] uppercase tracking-[0.24em] font-semibold mb-3" style={{ color: GOLD }}>
            The maths
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatBox label="Monthly commitment" value={zar(ctx.monthly_commitment_zar)} icon={Wallet} tone={NAVY} />
            <StatBox label="Gross monthly income" value={zar(ctx.gross_income_zar)} icon={Landmark} tone={NAVY} />
            <StatBox label="Essential costs" value={zar(ctx.essential_costs_zar)} icon={Wallet} tone="#94A3B8" />
            <StatBox label="Disposable income" value={zar(ctx.disposable_income_zar)} icon={CheckCircle2} tone={style.color} />
          </div>
          <div className="mt-3 rounded-xl p-3 border border-slate-100 bg-slate-50 flex items-center gap-3 flex-wrap">
            <div className="text-[11px] text-slate-500">Commitment as % of disposable</div>
            <div className="flex-1 min-w-[140px] h-2 rounded-full overflow-hidden" style={{ background: "#E2E8F0" }}>
              <div
                className="h-2 rounded-full"
                style={{
                  width: `${Math.min(100, (ctx.commitment_ratio || 0) * 100)}%`,
                  background: style.color,
                }}
              />
            </div>
            <div className="font-mono font-semibold text-[13px]" style={{ color: style.color }}>
              {Math.round((ctx.commitment_ratio || 0) * 100)}%
            </div>
          </div>
          <div className="mt-3 text-[11px] text-slate-500 leading-relaxed">
            Altara flags "additional pressure" when the monthly commitment is more than {Math.round((ctx.threshold || 0.25) * 100)}% of your disposable income. This is a context signal only — it does not stop you deciding.
          </div>
        </div>
      )}

      {/* Missing profile hint */}
      {!ctx.profile_complete && (
        <div className="rounded-2xl border border-slate-100 bg-white p-4 lg:p-5 flex items-center gap-4 flex-wrap" data-testid="check-profile-missing">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${GOLD}22` }}>
            <UserCog className="w-5 h-5" style={{ color: NAVY }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-semibold" style={{ color: NAVY }}>Complete your Financial Profile for a personalised check</div>
            <div className="text-[11.5px] text-slate-600 mt-0.5">Takes about two minutes. Nothing is shared with institutions.</div>
          </div>
          <Link
            to="/wallet/profile"
            data-testid="check-profile-cta"
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[12px] font-semibold text-white"
            style={{ background: NAVY }}
          >
            Complete profile <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      )}

      {/* Consumer note */}
      {!done && (
        <div className="rounded-2xl border border-slate-100 bg-white p-4 lg:p-5" data-testid="check-note">
          <label htmlFor="check-note" className="text-[11.5px] font-semibold uppercase tracking-widest" style={{ color: GOLD }}>
            Anything you want to note before deciding? (optional)
          </label>
          <textarea
            id="check-note"
            data-testid="check-note-input"
            value={note}
            onChange={e => setNote(e.target.value.slice(0, 500))}
            placeholder="E.g. 'I want to ask about the exit fees before I commit.'"
            rows={3}
            className="mt-2 w-full rounded-xl border border-slate-200 p-3 text-[12.5px] focus:outline-none focus:ring-2"
            style={{ color: NAVY }}
          />
        </div>
      )}

      {/* Action */}
      {done ? (
        <div className="rounded-2xl border p-4 flex items-center gap-3" style={{ background: "#ECFDF5", borderColor: "#A7F3D0" }} data-testid="check-completed-banner">
          <CheckCircle2 className="w-5 h-5" style={{ color: "#059669" }} />
          <div className="text-[13px]" style={{ color: "#065F46" }}>
            <div className="font-semibold">Consumer Context Check reviewed</div>
            <div className="text-[12px] mt-0.5">Recorded to the evidence trail. Move to Act to record your decision.</div>
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-100 bg-white p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="check-action-row">
          <div className="text-[12px] text-slate-600 leading-relaxed">
            When you've read the context, advance to <span className="font-semibold" style={{ color: NAVY }}>Act</span> to record your decision.
          </div>
          <button
            onClick={submit}
            disabled={busy}
            data-testid="check-review-btn"
            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-semibold text-white disabled:opacity-40"
            style={{ background: NAVY }}
          >
            {busy ? "Recording…" : "I've reviewed the fit — go to Act"}
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
      {err && <div className="text-[12px] text-red-700" data-testid="check-error">{err}</div>}
    </section>
  );
}

function StatBox({ label, value, icon: Icon, tone }) {
  return (
    <div className="rounded-xl p-3 bg-slate-50">
      <div className="flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" style={{ color: tone }} />
        <div className="text-[10.5px] uppercase tracking-widest font-semibold text-slate-500">{label}</div>
      </div>
      <div className="mt-1 font-mono font-semibold text-[14px]" style={{ color: NAVY }}>{value}</div>
    </div>
  );
}
